import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form, Badge, Pagination } from 'react-bootstrap';
import { getSupportTickets, createSupportTicket, useUser, useAppDispatch } from '../../redux';
import { showToast, getErrorMessage } from '../../utils/toast';
import Spinner from '../common/Spinner';



const SupportTicketsTab = () => {
  const [message, setMessage] = useState('');
  const [captcha, setCaptcha] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const dispatch = useAppDispatch();
  const { loading, tickets } = useUser();

  useEffect(() => {
    dispatch(getSupportTickets());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      showToast.warning('Please enter your message');
      return;
    }
    if (!captcha) {
      showToast.warning('Please verify that you are not a robot');
      return;
    }

    try {
      const result = await dispatch(createSupportTicket(message));
      
      if (result.type.endsWith('fulfilled')) {
        showToast.success('Support ticket created successfully!');
        setMessage('');
        setCaptcha(false);
        setCurrentPage(1); // Reset to first page after creating ticket
        // Refresh tickets list
        dispatch(getSupportTickets());
      } else if (result.type.endsWith('rejected')) {
        showToast.error(result.payload || 'Failed to create support ticket');
      }
    } catch (error) {
      showToast.error(getErrorMessage(error));
    }
  };

  // Pagination logic
  const totalPages = Math.ceil((tickets?.length || 0) / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = tickets?.slice(indexOfFirstItem, indexOfLastItem) || [];

  // Reset to first page when items per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRefresh = () => {
    dispatch(getSupportTickets());
    setCurrentPage(1); // Reset to first page after refresh
  };

  return (
    <div className="support-tickets-tab p-3">
      <div className="d-flex justify-content-center">
        <Card style={{ width: '100%'}} className="shadow-sm mb-4">
          <Card.Header className="bg-primary text-white text-center py-3">
            <h4 className="mb-0 fw-bold">Create Support Ticket</h4>
          </Card.Header>
          <Card.Body className="p-4">
            <Form onSubmit={handleSubmit}>
              <div className="mb-3">
                <h6 className="text-primary fw-bold mb-2 border-bottom pb-1">How can we help you?</h6>
                <Form.Group controlId="supportMessage">
                  <Form.Label className="small fw-semibold">Your Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Please describe your issue or question in detail..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    required
                    className="border-0 bg-light"
                    style={{ minHeight: '120px' }}
                  />
                </Form.Group>
              </div>
              
              {/* Captcha and Submit Section */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="d-flex align-items-center">
                  <div className="border rounded p-2 bg-light me-2">
                    <Form.Check
                      type="checkbox"
                      id="captchaCheck"
                      label="I'm not a robot"
                      checked={captcha}
                      onChange={e => setCaptcha(e.target.checked)}
                      required
                      className="mb-0"
                    />
                  </div>
                  <span className="text-muted small">reCAPTCHA</span>
                </div>
                
                <Button 
                  type="submit" 
                  variant="primary" 
                  size="md" 
                  className="px-4 fw-bold" 
                  disabled={!captcha || !message || loading}
                >
                  {loading ? 'Creating...' : 'Create Ticket'}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <div className="d-flex justify-content-center">
        <Card style={{ width: '100%'}} className="shadow-sm">
          <Card.Header className="bg-primary text-white py-3">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-bold">Your Support Tickets</h4>
              <Button 
                variant="light" 
                size="sm" 
                onClick={handleRefresh}
                disabled={loading}
                className="fw-semibold"
              >
                {loading ? <Spinner size="sm" /> : 'Refresh'}
              </Button>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0" hover>
                <thead className="table-light">
                  <tr>
                    <th className="px-4 py-3 fw-semibold text-dark" style={{minWidth: 160}}>Date Created</th>
                    <th className="px-4 py-3 fw-semibold text-dark" style={{minWidth: 300}}>Customer Request</th>
                    <th className="px-4 py-3 fw-semibold text-dark" style={{minWidth: 300}}>Response by IBRLive</th>
                    <th className="px-4 py-3 fw-semibold text-dark" style={{minWidth: 120}}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="text-center py-5">
                        <Spinner />
                        <p className="mt-2 text-muted">Loading support tickets...</p>
                      </td>
                    </tr>
                  ) : (
                    currentTickets && currentTickets.length > 0 ? currentTickets.map((ticket, idx) => (
                      <tr key={ticket.id || idx} className="border-bottom">
                        <td className="px-4 py-3">
                          <div className="small text-muted">
                            {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : ''}
                          </div>
                          <div className="small text-muted">
                            {ticket.created_at ? new Date(ticket.created_at).toLocaleTimeString() : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div style={{ maxWidth: '300px', wordWrap: 'break-word' }} className="text-dark">
                            {ticket.request_message}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div style={{ maxWidth: '300px', wordWrap: 'break-word' }} className={ticket.response_message ? 'text-dark' : 'text-muted fst-italic'}>
                            {ticket.response_message || 'Awaiting response...'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            bg={ticket.status === 'pending' ? 'warning' : ticket.status === 'resolved' ? 'success' : 'secondary'}
                            className="px-2 py-1"
                          >
                            {ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                          </Badge>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <div className="text-muted">
                            <p className="mb-1">No support tickets found.</p>
                            <small>Create your first ticket using the form above.</small>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          
          {/* Pagination */}
          {!loading && tickets && tickets.length > itemsPerPage && (
            <Card.Footer className="bg-light">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <small className="text-muted me-3">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, tickets.length)} of {tickets.length} tickets
                  </small>
                  <div className="d-flex align-items-center">
                    <small className="text-muted me-2">Show:</small>
                    <Form.Select
                      size="sm"
                      style={{ width: 'auto' }}
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                    </Form.Select>
                    <small className="text-muted ms-2">per page</small>
                  </div>
                </div>
                
                <Pagination className="mb-0" size="sm">
                  <Pagination.First
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />

                  {/* Page numbers */}
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    const isCurrentPage = pageNumber === currentPage;

                    // Show first page, last page, current page, and pages around current page
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <Pagination.Item
                          key={pageNumber}
                          active={isCurrentPage}
                          onClick={() => handlePageChange(pageNumber)}
                        >
                          {pageNumber}
                        </Pagination.Item>
                      );
                    }

                    // Show ellipsis
                    if (
                      (pageNumber === currentPage - 2 && currentPage > 3) ||
                      (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
                    ) {
                      return <Pagination.Ellipsis key={pageNumber} disabled />;
                    }

                    return null;
                  })}

                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </Card.Footer>
          )}
        </Card>
      </div>
    </div>
  );
};

export default SupportTicketsTab; 